import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, AlertTriangle, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


export function TransactionFieldCensusPanel() {
  const [loading, setLoading] = useState(false);
  const [census, setCensus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const runCensus = async () => {
    setLoading(true);
    setCensus(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-field-census');
      setCensus(data);
      if (data.total_array_documents === 0) {
        toast.success('No array-shaped fields found');
      } else {
        toast.warning(`${data.total_array_documents} array-shaped document(s) found`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-field-migration');
      setPreview(data);
      if (data.total_affected === 0) {
        toast.success('No documents need migration');
      } else {
        toast.info(`${data.total_affected} document(s) would be migrated`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setApplyResult(null);
    try {
      const { data } = await api.post('/admin/diagnostics/transaction-field-migration');
      setApplyResult(data);
      setPreview(null);
      if (data.total_updated > 0) {
        toast.success(`Migrated ${data.total_updated} document(s)`);
      } else {
        toast.info('No changes (0 updated)');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Database className="h-5 w-5" />
          Transaction Field Shape Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Counts transaction documents by BSON type (string / array / null) for text fields
          that Berlin Group banks can store as arrays. Migration joins arrays into strings.
        </p>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={runCensus} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Run Census
          </Button>
          <Button size="sm" variant="outline" onClick={runPreview} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Preview Migration
          </Button>
        </div>

        {census && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {census.total_array_documents === 0
                ? <><CheckCircle2 className="h-4 w-4 text-green-600" /> <span className="text-sm font-medium">All fields are scalar strings</span></>
                : <><AlertTriangle className="h-4 w-4 text-amber-500" /> <span className="text-sm font-medium">{census.total_array_documents} array-shaped document(s)</span></>
              }
            </div>
            {Object.entries(census.fields).map(([field, sources]) => (
              <div key={field} className="border rounded p-3">
                <h4 className="text-sm font-semibold mb-1">{field}</h4>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {Object.entries(sources).map(([source, types]) => (
                    <div key={source}>
                      <span className="font-medium">{source}:</span>{' '}
                      {Object.entries(types).map(([t, c]) => (
                        <Badge key={t} variant={t === 'array' ? 'destructive' : 'secondary'} className="mr-1 text-xs">
                          {t}: {c}
                        </Badge>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Migration Preview</h4>
            {preview.total_affected === 0 ? (
              <p className="text-sm text-green-600">No documents need migration.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-1 text-sm">
                  {Object.entries(preview.affected_by_field).map(([field, count]) => (
                    <div key={field}>
                      <span className="font-medium">{field}:</span> {count} doc(s)
                    </div>
                  ))}
                </div>
                <p className="text-sm font-medium">Total: {preview.total_affected} document(s)</p>
                <Button size="sm" variant="destructive" onClick={runApply} disabled={applying}>
                  {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Apply Migration
                </Button>
              </>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Migration applied: {applyResult.total_updated} updated</span>
            </div>
            {applyResult.entries.map((entry, i) => (
              <div key={i} className="text-xs text-muted-foreground">
                {entry.field}: {entry.documents_updated} document(s)
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
